import { useEffect } from 'react'
import { Box, Card, CardHeader, CardBody, Heading, Text } from '@chakra-ui/react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ParentTicketForm } from '@/components/forms/ParentTicketForm'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useParentTicketDraftStore } from '@/store/parentTicketDraft'
import type { HelpdeskRequest, HelpdeskRequestType } from '@/types/helpdesk'

const REQUEST_TYPES: HelpdeskRequestType[] = [
  'Break fix',
  'Ad Hoc',
  'Adhoc Reliever',
  'Installing',
  'Preventive Maintenance',
  'Follow Up',
]

function mapRowToHelpdeskRequest(row: Record<string, unknown>): HelpdeskRequest {
  // Use request_number (ticket_requests.request_number) so search API finds the request; id is numeric DB id
  const id = String(row.request_number ?? row.id ?? '')
  const clientName = (row.client_name ?? row.contact_name ?? '') as string
  const serviceTypeRaw =
    typeof row.service_type === 'object' && row.service_type !== null && 'name' in (row.service_type as object)
      ? String((row.service_type as { name?: string }).name ?? '')
      : String(row.service_type ?? '')
  const requestType = REQUEST_TYPES.includes(serviceTypeRaw as HelpdeskRequestType)
    ? (serviceTypeRaw as HelpdeskRequestType)
    : 'Ad Hoc'
  const subject = (row.description ?? '') as string
  const description = (row.description ?? '') as string
  const requestedAt = (row.submitted_at ?? row.requested_at ?? '') as string
  return {
    id,
    clientName,
    clientOrg: (row.client_org ?? row.client_name) as string | undefined,
    serialNumber: (row.serial_number ?? row.serialNumber) as string | undefined,
    unitModel: (row.unit_model ?? row.unitModel) as string | undefined,
    requestType,
    subject,
    description,
    requestedAt,
    status: 'ACKNOWLEDGED',
    contactName: (row.contact_name ?? row.contactName) as string | undefined,
    contactEmail: (row.contact_email ?? row.contactEmail) as string | undefined,
    requestSource: (row.request_source ?? row.requestSource) as string | undefined,
  }
}

export default function ParentTicketCreatePage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const location = useLocation()
  const hydrateFromRequest = useParentTicketDraftStore((s) => s.hydrateFromRequest)

  useEffect(() => {
    const fromRequest = location.state?.fromRequest as Record<string, unknown> | undefined
    if (fromRequest && typeof fromRequest === 'object') {
      const request = mapRowToHelpdeskRequest(fromRequest)
      hydrateFromRequest(request)
    }
  }, [location.state, hydrateFromRequest])

  const handleSuccess = (ticketId: string) => {
    if (ticketId) {
      navigate(`/tickets/${ticketId}/view`)
    } else {
      navigate('/tickets')
    }
  }

  const handleCancel = () => {
    navigate('/tickets')
  }

  return (
    <Box p={4} h="full" overflow="hidden" display="flex" flexDirection="column">
      <Box minW={0} display="flex" flexDirection="column" overflow="hidden" flex="1">
        <Card h="full" display="flex" flexDirection="column">
          <CardHeader flexShrink={0}>
            <Heading size="md">Create New Ticket</Heading>
            <Text fontSize="sm" color={colors.text} mt={1}>
              Create a ticket from a request and assign to agents or teams
            </Text>
          </CardHeader>
          <CardBody overflowY="auto" flex="1" minH={0} px={{ base: 4, md: 5 }} py={4}>
            <ParentTicketForm onSuccess={handleSuccess} onCancel={handleCancel} />
          </CardBody>
        </Card>
      </Box>
    </Box>
  )
}
